import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import { AlertTriangle, X } from 'lucide-react';
import { CustomModal } from '../../ui';
import NewRegModal from '../../../pages/dashboard/home/NewRegModal';
import RegSubModal from '../../../pages/dashboard/home/RegSubModal';
import RegRenewalModal from '../../../pages/dashboard/home/RegRenewalModal';
import RegResumptionModal from '../../../pages/dashboard/home/RegResumptionModal';
import successGif from '../../../assets/images/success.json';
import UserService from '../../../services/user.service';
import { setCart, setRenewal, setResume } from '../../../redux/reducers/userSlice';
import { RootState } from '../../../redux/rootReducer';
import {
    PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY,
    PARENT_POST_SIGNUP_ENROLLMENT_KEY,
} from '../../../constants/sessionStorageKeys';
import { resolvePackageForChild } from '../../../utils/constants';
import {
    modalBody,
    modalCloseBtn,
    modalPrimaryBtn,
    modalSecondaryBtn,
    modalShell,
    modalSubtitle,
    modalTitle,
} from '../../ui/modalDesign';

interface RegistrationContextValue {
    openAddChild: () => void;
    openAddChildOnly: () => void;
    openAddChildWithWarning: () => void;
}

const RegistrationContext = createContext<RegistrationContextValue>({
    openAddChild: () => undefined,
    openAddChildOnly: () => undefined,
    openAddChildWithWarning: () => undefined,
});

export function useRegistration() {
    return useContext(RegistrationContext);
}

function childHasCompletedRegistration(child: any): boolean {
    const programs = child?.programs;
    if (!Array.isArray(programs) || programs.length === 0) return false;
    return programs.some((p: any) => Boolean(p?.isPaid) || p?.isCompleted === true);
}

export function RegistrationProvider({ children }: { children: React.ReactNode }) {
    const { child, resumeChildReg } = useSelector((state: RootState) => state.user);
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const dispatch = useDispatch();
    const location = useLocation();

    const [toggleRegModal, setToggleRegModal] = useState(false);
    const [regTab, setRegTab] = useState(0);
    const [childInfo, setChildInfo] = useState<any>({});
    const [successModal, setSuccessModal] = useState(false);
    const [isRenewing, setIsRenewing] = useState(false);
    const [isClosable, setIsClosable] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [childOnlyMode, setChildOnlyMode] = useState(false);

    const getPackages = async () => {
        try {
            const response = await userService.getAllPackages();
            if (!response.status) return;
            const childAge = child?.age || child?.child?.age;
            resolvePackageForChild(response.data || [], childAge, 1);
        } catch {
            /* ignore */
        }
    };

    const dismissAddChildPrompt = () => {
        try {
            sessionStorage.setItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY, '1');
        } catch {
            /* ignore */
        }
    };

    const openAddChildForm = useCallback(() => {
        setChildOnlyMode(false);
        setRegTab(0);
        setIsRenewing(false);
        setIsClosable(true);
        setToggleRegModal(true);
    }, []);

    const openAddChildOnly = useCallback(() => {
        setChildOnlyMode(true);
        setRegTab(0);
        setIsRenewing(false);
        setIsClosable(true);
        setToggleRegModal(true);
    }, []);

    const openAddChildWithWarning = useCallback(() => {
        setChildOnlyMode(false);
        setShowWarningModal(true);
    }, []);

    const closeRegToggleModal = () => {
        if (!isClosable) return;
        setRegTab(0);
        setToggleRegModal(false);
        setChildOnlyMode(false);
        dispatch(setRenewal(null));
        dispatch(setResume(null));
        dismissAddChildPrompt();
        window.dispatchEvent(new Event('ryd-reg-flow-ended'));
    };

    const closeRegTabByBtnClick = () => {
        setRegTab(0);
        setToggleRegModal(false);
        setChildOnlyMode(false);
        dispatch(setRenewal(null));
        setIsClosable(true);
        dismissAddChildPrompt();
        window.dispatchEvent(new Event('ryd-reg-flow-ended'));
    };

    const finishChildOnly = () => {
        setRegTab(0);
        setToggleRegModal(false);
        setChildOnlyMode(false);
        setIsClosable(true);
        window.dispatchEvent(new Event('ryd-reg-flow-ended'));
    };

    const handleNext = () => setRegTab((prev) => prev + 1);
    const handlePrevious = () => setRegTab((prev) => prev - 1);

    const prevResumeChildReg = useRef(resumeChildReg);
    useEffect(() => {
        if (resumeChildReg) {
            setRegTab(0.7);
            setToggleRegModal(true);
            setIsRenewing(false);
            setChildInfo(resumeChildReg);
            getPackages();
        } else if (prevResumeChildReg.current) {
            setRegTab(0);
            setToggleRegModal(false);
        }
        prevResumeChildReg.current = resumeChildReg;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeChildReg]);

    const prevRenewalChild = useRef(child);
    useEffect(() => {
        if (child) {
            setRegTab(0.5);
            setToggleRegModal(true);
            setIsRenewing(true);
            setChildInfo(child);
            getPackages();
        } else if (prevRenewalChild.current) {
            setRegTab(0);
            setToggleRegModal(false);
        }
        prevRenewalChild.current = child;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [child]);

    useEffect(() => {
        if (location.pathname !== '/parent/home') return;
        if (userInfo?.mustResetPassword) return;
        if (child || resumeChildReg) return;
        try {
            if (sessionStorage.getItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY) === '1') return;
            const intent = sessionStorage.getItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY);
            // Math / Other open the private lesson modal on Home instead.
            if (intent === 'mathematics' || intent === 'other') return;
        } catch {
            /* ignore */
        }

        let cancelled = false;
        (async () => {
            try {
                const response = await userService.getChildren();
                if (cancelled || !response?.status || !Array.isArray(response.data)) return;
                const hasRegisteredChild = response.data.some((c: any) => childHasCompletedRegistration(c));
                if (!hasRegisteredChild) {
                    setRegTab(0);
                    setIsRenewing(false);
                    setIsClosable(true);
                    setToggleRegModal(true);
                }
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [child, resumeChildReg, location.pathname, userInfo?.mustResetPassword]);

    return (
        <RegistrationContext.Provider value={{ openAddChild: openAddChildForm, openAddChildOnly, openAddChildWithWarning }}>
            {children}

            {toggleRegModal && !userInfo?.mustResetPassword && (
                <CustomModal modalStyle={modalShell} closeModal={closeRegToggleModal}>
                    {regTab === 0 && (
                        <NewRegModal
                            setChildInfo={(data: any) => setChildInfo(data)}
                            handleNext={handleNext}
                            closeModalOnOutsideClick={(data: boolean) => setIsClosable(data)}
                            closeRegModal={closeRegTabByBtnClick}
                            childOnly={childOnlyMode}
                            onChildOnlyComplete={finishChildOnly}
                            onDuplicateChildAddProgram={(dupChild: any) => {
                                setRegTab(0);
                                setToggleRegModal(false);
                                setChildOnlyMode(false);
                                setTimeout(() => {
                                    dispatch(setRenewal(dupChild));
                                    setRegTab(0.5);
                                    setToggleRegModal(true);
                                    setIsRenewing(true);
                                    setChildInfo(dupChild);
                                    getPackages();
                                }, 100);
                            }}
                            onDuplicateChildResume={(dupChild: any) => {
                                setRegTab(0);
                                setToggleRegModal(false);
                                setChildOnlyMode(false);
                                setTimeout(() => dispatch(setResume(dupChild)), 100);
                            }}
                        />
                    )}
                    {regTab === 0.5 && (
                        <RegRenewalModal
                            setChildInfo={(_arg1: number, _arg2: number, _cohortId: number) => {
                                setChildInfo({
                                    ...childInfo,
                                    programs: [{ ...childInfo.programs[0], day: _arg1, time: _arg2, cohortId: _cohortId }],
                                });
                            }}
                            handleNext={() => setRegTab(1)}
                            closeModalOnOutsideClick={(data: boolean) => setIsClosable(data)}
                            closeRegTab={() => {
                                setRegTab(0);
                                setToggleRegModal(false);
                                dispatch(setRenewal(null));
                            }}
                        />
                    )}
                    {regTab === 0.7 && (
                        <RegResumptionModal
                            setChildInfo={(_arg1: any, _arg2: any, _cohortId: number) => {
                                setChildInfo({
                                    ...childInfo,
                                    programs: [{ ...childInfo.programs[0], day: _arg1, time: _arg2, cohortId: _cohortId }],
                                });
                            }}
                            handleNext={() => setRegTab(1)}
                            closeModalOnOutsideClick={(data: boolean) => setIsClosable(data)}
                            closeRegTab={() => {
                                setRegTab(0);
                                setToggleRegModal(false);
                                dispatch(setResume(null));
                            }}
                        />
                    )}
                    {regTab === 1 && (
                        <RegSubModal
                            childInfo={childInfo}
                            handlePrevious={handlePrevious}
                            isRenewing={isRenewing}
                            closeRegTab={() => {
                                setRegTab(0);
                                setToggleRegModal(false);
                                dispatch(setCart(true));
                                dispatch(setRenewal(null));
                            }}
                            setSuccessModal={() => setSuccessModal(true)}
                        />
                    )}
                </CustomModal>
            )}

            {showWarningModal && !userInfo?.mustResetPassword && (
                <CustomModal
                    modalStyle={`${modalShell} max-w-lg`}
                    closeModal={() => setShowWarningModal(false)}
                >
                    <div className={`relative ${modalBody}`}>
                        <button
                            type="button"
                            aria-label="Close"
                            className={modalCloseBtn}
                            onClick={() => setShowWarningModal(false)}
                        >
                            <X size={18} />
                        </button>

                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                            <AlertTriangle size={22} />
                        </div>
                        <h1 className={modalTitle}>Important Notice</h1>
                        <p className={`${modalSubtitle} text-left`}>
                            Only use this when registering a{' '}
                            <span className="font-semibold text-ryd-headerTextPrimary">new child</span>. If your child
                            already completed a cohort, add another program from their card instead.
                        </p>
                        <ul className="mt-4 space-y-2 rounded-[14px] border border-[#efe6f1] bg-[#FBF8FC] px-4 py-3 text-[13px] text-[#476788]">
                            <li>1. Go to your dashboard or Children page</li>
                            <li>
                                2. Under your child&apos;s name, click{' '}
                                <span className="font-semibold text-ryd-primary">&quot;Add a Program&quot;</span>
                            </li>
                        </ul>

                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={() => setShowWarningModal(false)}
                                className={modalSecondaryBtn}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowWarningModal(false);
                                    openAddChildForm();
                                }}
                                className={modalPrimaryBtn}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}

            {successModal && (
                <CustomModal
                    modalStyle={`${modalShell} max-w-md`}
                    closeModal={() => setSuccessModal(false)}
                >
                    <div className={`relative ${modalBody} text-center`}>
                        <button
                            type="button"
                            aria-label="Close"
                            className={modalCloseBtn}
                            onClick={() => {
                                setSuccessModal(false);
                                window.dispatchEvent(new Event('ryd-reg-flow-ended'));
                            }}
                        >
                            <X size={18} />
                        </button>
                        <div className="mx-auto h-[120px] w-[120px] sm:h-[140px] sm:w-[140px]">
                            <Lottie animationData={successGif} />
                        </div>
                        <h1 className={modalTitle}>Successful!</h1>
                        <p className={modalSubtitle}>
                            Go to cart to complete payment, or use Add New Child to enroll another learner.
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setSuccessModal(false);
                                window.dispatchEvent(new Event('ryd-reg-flow-ended'));
                            }}
                            className={`${modalPrimaryBtn} mt-6`}
                        >
                            Done
                        </button>
                    </div>
                </CustomModal>
            )}
        </RegistrationContext.Provider>
    );
}
